import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import "../../styles/llm-cost-analysis.css";

const leftSectionIds = ["synthesis", "balances", "bankHistory"];
const rightSectionIds = ["bankStatements"];

const seriesPalette = {
  balance: { stroke: "#0ea5e9", fill: "#e0f2fe", dot: "#0284c7" },
  income: { stroke: "#22c55e", fill: "#dcfce7", dot: "#16a34a" },
  expense: { stroke: "#f97316", fill: "#ffedd5", dot: "#ea580c" },
};

const incomeCategoryOptions = [
  "Income",
  "Ineligible Income",
  "Other Income",
  "Rental Income",
  "Salary",
  "DC(Direct Credit)",
];

const expenseCategoryOptions = [
  "BEE(Basic Essential Expenses)",
  "BQL(Basic Quality of Life)",
  "CE(Credit Related Expenses)",
  "DE(Direct Expenses)",
  "HOUSING",
];

function parseNumericValue(value) {
  if (typeof value === "number") return value;
  if (value && typeof value === "object" && typeof value.amount === "number") {
    return value.amount;
  }

  const normalized = Number(String(value ?? "").replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(normalized) ? normalized : 0;
}

function normalizeCategoryValues(categories) {
  if (!categories || typeof categories !== "object" || Array.isArray(categories)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(categories).map(([key, value]) => [key, parseNumericValue(value)])
  );
}

function getSeriesEntries(seriesSource) {
  if (Array.isArray(seriesSource)) return seriesSource;

  if (seriesSource && typeof seriesSource === "object") {
    if (Array.isArray(seriesSource.series)) return seriesSource.series;

    const nestedArray = Object.values(seriesSource).find((value) =>
      Array.isArray(value)
    );
    if (Array.isArray(nestedArray)) return nestedArray;

    const nestedSeriesObject = Object.values(seriesSource).find(
      (value) => value && typeof value === "object" && Array.isArray(value.series)
    );
    if (nestedSeriesObject) return nestedSeriesObject.series;
  }

  return [];
}

function normalizeTimelineItems(items = []) {
  return items.map((item, index) => {
    if ("dateRange" in item || "income" in item || "expense" in item) {
      return {
        id: item.id ?? `range-${index + 1}`,
        dateRange: item.dateRange ?? item.date_range ?? "",
        income: parseNumericValue(item.income),
        expense: parseNumericValue(item.expense),
        kind: item.kind ?? "flow",
      };
    }

    return {
      id: item.id ?? `event-${index + 1}`,
      date: item.date,
      label: item.label,
      kind: item.kind ?? "event",
    };
  });
}

function normalizeKeyValueItems(items, fallbackId = "item") {
  const sourceItems = Array.isArray(items) ? items : items?.items;
  if (!Array.isArray(sourceItems)) return [];

  return sourceItems.map((item, index) => ({
    ...item,
    id: Array.isArray(item.id)
      ? String(item.id[0] ?? `${fallbackId}-${index + 1}`)
      : item.id ?? `${fallbackId}-${index + 1}`,
  }));
}

function normalizeIndicatorItems(items = []) {
  const sourceItems = Array.isArray(items) ? items : [];

  return sourceItems.map((item, index) => ({
    ...item,
    id: Array.isArray(item.id)
      ? String(item.id[0] ?? `indicator-${index + 1}`)
      : item.id ?? `indicator-${index + 1}`,
    status: item.status ?? "warn",
  }));
}

function normalizeMatrixBlock(block) {
  if (!block || block.type !== "matrix") return block;

  const rowsUseChildren =
    Array.isArray(block.rows) && block.rows.some((row) => Array.isArray(row.children));

  if (rowsUseChildren) {
    return {
      ...block,
      rows: block.rows.flatMap((row) => {
        const normalizedRows = [];

        if (Array.isArray(row.values) && row.values.length > 0) {
          normalizedRows.push({
            id: `${row.id}-summary`,
            group: row.label,
            label: row.label,
            values: row.values ?? [],
            total: row.total,
            isGroupSummary: true,
          });
        }

        normalizedRows.push(
          ...(row.children ?? []).map((child) => ({
            id: child.id,
            group: row.label,
            label: child.label,
            values: child.values ?? [],
            total: child.total,
            isGroupSummary: false,
          }))
        );

        return normalizedRows;
      }),
      meta: block.meta ?? { currency: "EUR" },
    };
  }

  return {
    ...block,
    rows: Array.isArray(block.rows) ? block.rows : [],
    meta: block.meta ?? { currency: "EUR" },
  };
}

function normalizeSeriesBlock(block) {
  if (!block || block.type !== "series") return block;

  const seriesEntries = getSeriesEntries(block.series);
  const usesMonthlyRows =
    seriesEntries.length > 0 && !seriesEntries[0]?.points && "label" in seriesEntries[0];

  if (usesMonthlyRows) {
    return {
      ...block,
      series: [
        {
          id: "balance",
          label: "Balance",
          points: seriesEntries.map((entry) => ({
            x: entry.label,
            y: Number(entry.balance ?? 0),
          })),
        },
        {
          id: "income",
          label: "Income",
          points: seriesEntries.map((entry) => ({
            x: entry.label,
            y: Number(entry.income ?? 0),
            categories: normalizeCategoryValues(entry.incomeCategories),
          })),
        },
        {
          id: "expense",
          label: "Expense",
          points: seriesEntries.map((entry) => ({
            x: entry.label,
            y: Number(entry.expense ?? 0),
            categories: normalizeCategoryValues(entry.expenseCategories),
          })),
        },
      ],
      meta: block.meta ?? { currency: "EUR" },
    };
  }

  return {
    ...block,
    series: seriesEntries.map((series, index) => ({
      id: series.id ?? `series-${index + 1}`,
      label: series.label ?? "Series",
      points: (series.points ?? []).map((point) => ({
        x: point.x ?? `${point.date}-01`,
        y: point.y ?? point.value ?? 0,
        categories: point.categories ?? {},
      })),
    })),
    meta: block.meta ?? { currency: "EUR" },
  };
}

function normalizeBlock(block) {
  if (!block) return block;

  if (block.type === "keyValueGrid") {
    return {
      ...block,
      items: normalizeKeyValueItems(block.items, block.id),
    };
  }

  if (block.type === "indicatorList") {
    return {
      ...block,
      items: normalizeIndicatorItems(block.items),
    };
  }

  if (block.type === "timeline") {
    return {
      ...block,
      items: normalizeTimelineItems(block.items),
    };
  }

  if (block.type === "table") {
    return { ...block, columns: block.columns ?? block.months ?? [] };
  }

  if (block.type === "matrix") return normalizeMatrixBlock(block);
  if (block.type === "series") return normalizeSeriesBlock(block);

  return block;
}

function normalizeReport(report) {
  if (!report) return null;

  return {
    reportId: report.reportId ?? `csv-${report.customerId ?? "report"}`,
    generatedAt: report.generatedAt ?? new Date().toISOString(),
    ...report,
    sections: (report.sections ?? []).map((section) => ({
      ...section,
      blocks: (section.blocks ?? []).map(normalizeBlock),
    })),
  };
}

function getReport(analysisResponse) {
  return normalizeReport(analysisResponse?.data || analysisResponse?.reports?.[0] || null);
}

function formatMoney(value) {
  if (!value || typeof value !== "object") return String(value ?? "");
  const { amount, currency } = value;

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "EUR",
      maximumFractionDigits: 2,
    }).format(amount ?? 0);
  } catch {
    return `${currency ?? ""} ${amount ?? 0}`.trim();
  }
}

function formatCurrencyAmount(amount, currency = "EUR") {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
      minimumFractionDigits: Math.abs(Number(amount ?? 0) % 1) > 0 ? 2 : 0,
    }).format(Number(amount ?? 0));
  } catch {
    return `${currency} ${formatCompactNumber(Number(amount ?? 0))}`.trim();
  }
}

function formatValue(value) {
  if (typeof value === "number") return formatCompactNumber(value);
  if (value && typeof value === "object" && "amount" in value && "currency" in value) {
    return formatMoney(value);
  }
  return String(value ?? "");
}

function formatIsoDate(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso);
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);
}

function formatCompactNumber(value) {
  if (typeof value !== "number") return String(value ?? "");
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: Math.abs(value % 1) > 0 ? 2 : 0,
  }).format(value);
}

function getColumnKey(column, index) {
  if (typeof column === "string") return column;
  return column?.id ?? column?.label ?? `column-${index + 1}`;
}

function getColumnLabel(column) {
  if (typeof column === "string") return column;
  return column?.label ?? "";
}

function getCompactColumnLabel(column) {
  const label = getColumnLabel(column);
  const parsed = new Date(`${label} 1`);

  if (!Number.isNaN(parsed.getTime())) {
    const month = new Intl.DateTimeFormat(undefined, { month: "short" }).format(parsed);
    return `${month} ${parsed.getFullYear()}`;
  }

  return label;
}

function getColumnDateRange(column) {
  if (typeof column === "string") return "";
  return column?.dateRange ?? "";
}

function getNumericAmount(value) {
  if (typeof value === "number") return value;
  if (value && typeof value === "object" && typeof value.amount === "number") {
    return value.amount;
  }
  return 0;
}

function shouldColorSignedRow(row) {
  const normalizedId = String(row.id ?? "").toLowerCase();
  const normalizedLabel = String(row.label ?? "").toLowerCase();
  return (
    ["evolution", "total"].includes(normalizedId) ||
    ["evolution", "total"].includes(normalizedLabel)
  );
}

function getSignedValueClass(row, value) {
  if (!shouldColorSignedRow(row)) return "csv-analysis-text-default";

  const amount = getNumericAmount(value);
  if (amount < 0) return "csv-analysis-text-red";
  if (amount > 0) return "csv-analysis-text-green";
  return "csv-analysis-text-default";
}

function formatXAxisLabel(value) {
  const parsedDate = new Date(String(value ?? ""));
  if (!Number.isNaN(parsedDate.getTime())) {
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      year: "2-digit",
    }).format(parsedDate);
  }

  const [month, year] = String(value ?? "").split(" ");
  if (!month) return String(value ?? "");
  const shortMonth = month.slice(0, 3);
  const shortYear = year ? year.slice(-2) : "";
  return shortYear ? `${shortMonth} ${shortYear}` : shortMonth;
}

function mapSeriesPoints(series, width, height, padding, minY, maxY, pointCount) {
  const dy = maxY - minY || 1;

  return (series.points ?? [])
    .map((point, index) => {
      const nx = pointCount > 1 ? index / (pointCount - 1) : 0.5;
      const px = padding + nx * (width - padding * 2);
      const py = height - padding - ((point.y - minY) / dy) * (height - padding * 2);
      return `${px.toFixed(1)},${py.toFixed(1)}`;
    })
    .map((mappedPoint, index) => ({
      point: mappedPoint,
      source: series.points[index],
    }));
}

function formatSelectionLabel(selectedOptions, defaultLabel) {
  if (!selectedOptions.length) return defaultLabel;
  if (selectedOptions.length === 1) return selectedOptions[0];
  return `${selectedOptions.length} selected`;
}

function getSeriesLabel(series, selectedIncomeCategories, selectedExpenseCategories) {
  if (series.id === "income") {
    return formatSelectionLabel(selectedIncomeCategories, "Income");
  }
  if (series.id === "expense") {
    return formatSelectionLabel(selectedExpenseCategories, "Expense");
  }
  return series.label;
}

function getSeriesScaleKey(seriesId) {
  return seriesId === "balance" ? "balance" : "flow";
}

function getSelectedValue(point, selectedCategories) {
  if (!selectedCategories.length) return Number(point.y ?? 0);

  return selectedCategories.reduce(
    (sum, category) => sum + Number(point.categories?.[category] ?? 0),
    0
  );
}

function getScaleBounds(seriesList, scaleKey) {
  const values = seriesList
    .filter((series) => getSeriesScaleKey(series.id) === scaleKey)
    .flatMap((series) => series.points?.map((point) => point.y) ?? []);

  if (!values.length) return { min: 0, max: 0 };

  return {
    min: Math.min(...values),
    max: Math.max(...values),
  };
}

const SectionPanel = ({ title, children }) => (
  <section className="csv-modal-section-panel">
    <div className="csv-modal-section-title">
      <span />
      <div>{title}</div>
    </div>
    <div className="csv-modal-section-content">{children}</div>
  </section>
);

const BlockCard = ({ title, prompt, children, className = "" }) => (
  <div className={`csv-modal-block ${className}`}>
    <div className="csv-modal-block-head">
      <div>{title}</div>
      {prompt ? <p>{prompt}</p> : null}
    </div>
    <div className="csv-modal-block-body">{children}</div>
  </div>
);

const KeyValueGridBlock = ({ block }) => {
  const visibleItems = (block.items ?? []).filter((item) => item.id !== "creditScore");

  return (
    <BlockCard title={block.title}>
      <div className="csv-modal-key-grid">
        {visibleItems.map((item) => (
          <div key={item.id} className="csv-modal-key-item">
            <div>{item.label}</div>
            <strong>{formatValue(item.value)}</strong>
          </div>
        ))}
      </div>
    </BlockCard>
  );
};

const IndicatorListBlock = ({ block }) => (
  <BlockCard title={block.title} prompt={block.prompt}>
    <div className="csv-modal-indicator-grid">
      {block.items.map((item) => (
        <div key={item.id} className="csv-modal-indicator">
          <span aria-hidden />
          <div>
            <strong>{item.label}</strong>
            <p>{item.detail}</p>
          </div>
        </div>
      ))}
    </div>
  </BlockCard>
);

const TimelineBlock = ({ block }) => {
  const items = block.items ?? [];
  const currency = block.meta?.currency ?? "EUR";
  const showsFlowRanges = items.every(
    (item) =>
      item.dateRange !== undefined ||
      item.income !== undefined ||
      item.expense !== undefined
  );

  if (showsFlowRanges) {
    const lineStart = 8;
    const lineEnd = 96;
    const arrowX = 99;
    const steps = Math.max(items.length - 1, 1);

    return (
      <BlockCard title={block.title} prompt={block.prompt}>
        <div className="csv-modal-timeline-shell">
          <svg viewBox="0 0 100 28" preserveAspectRatio="none">
            <defs>
              <marker
                id="csv-timeline-arrow"
                markerWidth="8"
                markerHeight="8"
                refX="6"
                refY="4"
                orient="auto"
              >
                <path d="M0,0 L8,4 L0,8 z" fill="#7a98ab" />
              </marker>
            </defs>
            <line
              x1={lineStart}
              y1="17"
              x2={arrowX}
              y2="17"
              stroke="#7a98ab"
              strokeWidth="0.35"
              markerEnd="url(#csv-timeline-arrow)"
            />
            <text x={lineStart + 1} y="21.8" fontSize="1.25" fill="#8ea4b2">
              1st day of month
            </text>
            {items.map((item, index) => {
              const x = lineStart + ((lineEnd - lineStart) * index) / steps;

              return (
                <g key={item.id}>
                  <text
                    x={x}
                    y="7.4"
                    textAnchor="middle"
                    fontSize="1.5"
                    fontWeight="700"
                    fill="#008000"
                  >
                    {formatCurrencyAmount(item.income, currency)}
                  </text>
                  <path d={`M ${x} 13.3 L ${x - 0.42} 12.2 L ${x + 0.42} 12.2 Z`} fill="#008000" />
                  <text
                    x={x}
                    y="9.9"
                    textAnchor="middle"
                    fontSize="1.5"
                    fontWeight="700"
                    fill="#FF0000"
                  >
                    {formatCurrencyAmount(item.expense, currency)}
                  </text>
                  <path d={`M ${x} 15.1 L ${x - 0.42} 14 L ${x + 0.42} 14 Z`} fill="#FF0000" />
                  <circle cx={x} cy="17" r="0.22" fill="#7a98ab" />
                  <text
                    x={x}
                    y="21.1"
                    textAnchor="middle"
                    fontSize="1.25"
                    fontWeight="700"
                    fill="#6f8797"
                  >
                    {item.dateRange}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </BlockCard>
    );
  }

  return null;
};

const TableBlock = ({ block }) => {
  const columns = block.columns ?? block.months ?? [];

  return (
    <BlockCard title={block.title} prompt={block.prompt} className="csv-modal-block-overflow">
      <div className="csv-modal-table-scroll">
        <table className="csv-modal-balance-table">
          <thead>
            <tr>
              <th> </th>
              {columns.map((col, index) => (
                <th key={getColumnKey(col, index)}>
                  <div>{getCompactColumnLabel(col)}</div>
                  {getColumnDateRange(col) ? <span>{getColumnDateRange(col)}</span> : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row) => (
              <tr key={row.id}>
                <td>{row.label}</td>
                {row.value !== undefined && columns.length > 0 ? (
                  <td colSpan={columns.length} className={getSignedValueClass(row, row.value)}>
                    {formatValue(row.value)}
                  </td>
                ) : Array.isArray(row.values) && row.values.length === 1 && columns.length > 1 ? (
                  <td colSpan={columns.length} className={getSignedValueClass(row, row.values[0])}>
                    {formatValue(row.values[0])}
                  </td>
                ) : Array.isArray(row.values) ? (
                  row.values.map((value, idx) => (
                    <td key={`${row.id}-${idx}`} className={getSignedValueClass(row, value)}>
                      {formatValue(value)}
                    </td>
                  ))
                ) : (
                  columns.map((column, idx) => (
                    <td key={`${row.id}-${getColumnKey(column, idx)}-${idx}`} />
                  ))
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </BlockCard>
  );
};

function getMonthKey(month, index) {
  if (typeof month === "string") return month;
  return month?.id ?? month?.label ?? `month-${index + 1}`;
}

function getMonthLabel(month) {
  if (typeof month === "string") return month;
  return month?.label ?? "";
}

function getMonthDateRange(month) {
  if (typeof month === "string") return "";
  return month?.dateRange ?? "";
}

const MatrixBlock = ({ block }) => {
  const groups = (block.rows ?? []).reduce((acc, row) => {
    acc[row.group] = acc[row.group] || [];
    acc[row.group].push(row);
    return acc;
  }, {});

  return (
    <BlockCard title={block.title} className="csv-modal-block-overflow">
      <div className="csv-modal-table-scroll">
        <table className="csv-modal-matrix-table">
          <thead>
            <tr>
              <th>Category</th>
              {block.months.map((month, index) => (
                <th key={getMonthKey(month, index)}>
                  <div>{getMonthLabel(month)}</div>
                  {getMonthDateRange(month) ? <span>{getMonthDateRange(month)}</span> : null}
                </th>
              ))}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(groups).map(([groupName, rows]) => (
              <Fragment key={groupName}>
                {rows.map((row) => {
                  const total = row.total ?? row.values.reduce(
                    (sum, value) => sum + getNumericAmount(value),
                    0
                  );

                  return (
                    <tr
                      key={row.id}
                      className={row.isGroupSummary ? "csv-modal-matrix-group" : ""}
                    >
                      <td>{row.label}</td>
                      {row.values.map((value, idx) => (
                        <td
                          key={`${row.id}-${idx}`}
                          className={getNumericAmount(value) < 0 ? "csv-analysis-text-red" : "csv-analysis-text-green"}
                        >
                          {formatValue(value)}
                        </td>
                      ))}
                      <td className={total < 0 ? "csv-analysis-text-red" : "csv-analysis-text-green"}>
                        {formatValue({ amount: total, currency: block.meta?.currency ?? "EUR" })}
                      </td>
                    </tr>
                  );
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="csv-modal-currency">Currency: {block.meta?.currency ?? ""}</div>
    </BlockCard>
  );
};

const MultiSelectDropdown = ({
  ariaLabel,
  color,
  fill,
  options,
  selectedOptions,
  setSelectedOptions,
  wide,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function handlePointerDown(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const toggleOption = (option) => {
    setSelectedOptions((current) => {
      const isSelected = current.includes(option);

      if (isSelected) {
        return current.length === 1 ? current : current.filter((item) => item !== option);
      }

      return [...current, option];
    });
  };

  return (
    <div ref={containerRef} className="csv-modal-select">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={wide ? "wide" : ""}
        style={{ backgroundColor: fill }}
      >
        <span>
          <i style={{ backgroundColor: color }} />
          <b>{formatSelectionLabel(selectedOptions, ariaLabel)}</b>
        </span>
        <strong>{open ? "^" : "v"}</strong>
      </button>

      {open ? (
        <div className="csv-modal-select-menu">
          <div>Select one or more</div>
          {options.map((option) => {
            const checked = selectedOptions.includes(option);

            return (
              <label key={option}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleOption(option)}
                />
                <span>{option}</span>
              </label>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

const SeriesBlock = ({ block }) => {
  const [selectedIncomeCategories, setSelectedIncomeCategories] = useState([incomeCategoryOptions[0]]);
  const [selectedExpenseCategories, setSelectedExpenseCategories] = useState([expenseCategoryOptions[0]]);
  const width = 760;
  const height = 220;
  const padding = 52;
  const seriesList = (block.series ?? []).map((series) => {
    if (series.id === "income") {
      return {
        ...series,
        label: formatSelectionLabel(selectedIncomeCategories, "Income"),
        points: (series.points ?? []).map((point) => ({
          ...point,
          y: getSelectedValue(point, selectedIncomeCategories),
        })),
      };
    }

    if (series.id === "expense") {
      return {
        ...series,
        label: formatSelectionLabel(selectedExpenseCategories, "Expense"),
        points: (series.points ?? []).map((point) => ({
          ...point,
          y: getSelectedValue(point, selectedExpenseCategories),
        })),
      };
    }

    return series;
  });
  const pointCount = Math.max(...seriesList.map((series) => series.points?.length ?? 0), 0);
  const steps = 4;
  const balanceBounds = getScaleBounds(seriesList, "balance");
  const flowBounds = getScaleBounds(seriesList, "flow");
  const balanceTicks = Array.from({ length: steps + 1 }, (_, index) => {
    const ratio = index / steps;
    return balanceBounds.max - (balanceBounds.max - balanceBounds.min) * ratio;
  });
  const flowTicks = Array.from({ length: steps + 1 }, (_, index) => {
    const ratio = index / steps;
    return flowBounds.max - (flowBounds.max - flowBounds.min) * ratio;
  });

  return (
    <BlockCard title={block.title} prompt={block.prompt}>
      <div className="csv-modal-series-controls">
        {seriesList.map((series) => {
          const colors = seriesPalette[series.id] ?? seriesPalette.balance;

          if (series.id === "income") {
            return (
              <div key={series.id}>
                <MultiSelectDropdown
                  ariaLabel="Income"
                  color={colors.dot}
                  fill={colors.fill}
                  options={incomeCategoryOptions}
                  selectedOptions={selectedIncomeCategories}
                  setSelectedOptions={setSelectedIncomeCategories}
                />
              </div>
            );
          }

          if (series.id === "expense") {
            return (
              <div key={series.id}>
                <MultiSelectDropdown
                  ariaLabel="Expense"
                  color={colors.dot}
                  fill={colors.fill}
                  options={expenseCategoryOptions}
                  selectedOptions={selectedExpenseCategories}
                  setSelectedOptions={setSelectedExpenseCategories}
                  wide
                />
              </div>
            );
          }

          return (
            <span
              key={series.id}
              className="csv-modal-series-pill"
              style={{ backgroundColor: colors.fill }}
            >
              <i style={{ backgroundColor: colors.dot }} />
              {getSeriesLabel(series, selectedIncomeCategories, selectedExpenseCategories)}
            </span>
          );
        })}
      </div>
      <div className="csv-modal-series-chart">
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
          <rect x="0" y="0" width={width} height={height} fill="#f8fbff" rx="12" />
          {balanceTicks.map((tick, index) => {
            const y = padding + ((height - padding * 2) * index) / steps;
            return (
              <g key={tick}>
                <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#dbeafe" strokeWidth="1" />
                <text x="10" y={y + 4} fontSize="11" fontWeight="700" fill="#475569">
                  {formatCompactNumber(tick)}
                </text>
              </g>
            );
          })}
          {flowTicks.map((tick, index) => {
            const y = padding + ((height - padding * 2) * index) / steps;
            return (
              <text
                key={`flow-${tick}-${index}`}
                x={width - 10}
                y={y + 4}
                textAnchor="end"
                fontSize="11"
                fontWeight="700"
                fill="#475569"
              >
                {formatCompactNumber(tick)}
              </text>
            );
          })}
          {Array.from({ length: pointCount }).map((_, index) => {
            const x = padding + (index * (width - padding * 2)) / Math.max(pointCount - 1, 1);
            return <line key={index} x1={x} y1={padding} x2={x} y2={height - padding} stroke="#eff6ff" />;
          })}
          {seriesList.map((series) => {
            const scaleBounds =
              getSeriesScaleKey(series.id) === "balance" ? balanceBounds : flowBounds;
            const mappedPoints = mapSeriesPoints(
              series,
              width,
              height,
              padding,
              scaleBounds.min,
              scaleBounds.max,
              pointCount
            );
            const poly = mappedPoints.map((entry) => entry.point).join(" ");
            const colors = seriesPalette[series.id] ?? seriesPalette.balance;

            if (!poly) return null;

            return (
              <g key={series.id}>
                <polyline
                  points={poly}
                  fill="none"
                  stroke={colors.stroke}
                  strokeWidth="3"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {mappedPoints.map(({ point, source }) => {
                  const [cx, cy] = point.split(",").map(Number);
                  return (
                    <circle
                      key={`${series.id}-${source.x}`}
                      cx={cx}
                      cy={cy}
                      r="4"
                      fill="#ffffff"
                      stroke={colors.dot}
                      strokeWidth="2"
                    />
                  );
                })}
              </g>
            );
          })}
          {(seriesList[0]?.points ?? []).map((point, index) => {
            const x = padding + (index * (width - padding * 2)) / Math.max(pointCount - 1, 1);
            return (
              <text key={point.x} x={x} y={height - 8} textAnchor="middle" fontSize="10" fill="#64748b">
                {formatXAxisLabel(point.x)}
              </text>
            );
          })}
        </svg>
      </div>
      <div className="csv-modal-axis-copy">
        <span>Left axis: Balance</span>
        <span>Right axis: Income / Expense category</span>
      </div>
    </BlockCard>
  );
};

const renderers = {
  keyValueGrid: KeyValueGridBlock,
  indicatorList: IndicatorListBlock,
  table: TableBlock,
  timeline: TimelineBlock,
  matrix: MatrixBlock,
  series: SeriesBlock,
};

const BlockRenderer = ({ block }) => {
  const Component = renderers[block.type];
  if (!Component) return null;
  return <Component block={block} />;
};

const CsvAnalysisReportModal = ({ show, onHide, analysisResponse }) => {
  const report = useMemo(() => getReport(analysisResponse), [analysisResponse]);
  const scoresData = analysisResponse?.score ?? {}

  const sectionById = useMemo(
    () =>
      Object.fromEntries(
        (report?.sections ?? []).map((section) => [section.id, section])
      ),
    [report]
  );

  return (
    <Modal
      show={show}
      dialogClassName="csv-analysis-modal-dialog"
      aria-labelledby="csv-analysis-modal-title"
      centered
      onHide={onHide}
    >
      <Modal.Body className="csv-analysis-modal-body">
        <div className="csv-modal-dashboard">
          <header className="csv-modal-hero">
            <div>
              <h1 id="csv-analysis-modal-title">{report?.title || "Analysis"}</h1>
              <p>
                Customer {report?.customerId || "-"} | {formatIsoDate(report?.generatedAt)}
              </p>
              <p>
                Credit Score: <strong>{scoresData?.creditScore ?? "-"}</strong>, Risk Score: <strong>{scoresData?.riskScore ?? "-"}</strong>
              </p>
            </div>
            <button
              type="button"
              className="csv-modal-close"
              aria-label="Close"
              onClick={onHide}
            >
              ×
            </button>
          </header>

          <div className="csv-modal-shell">
            <div className="csv-modal-grid">
              <div className="csv-modal-column">
                {leftSectionIds
                  .map((id) => sectionById[id])
                  .filter(Boolean)
                  .map((section) => (
                    <SectionPanel key={section.id} title={section.title}>
                      {section.blocks.map((block) => (
                        <BlockRenderer key={block.id} block={block} />
                      ))}
                    </SectionPanel>
                  ))}
              </div>

              <div className="csv-modal-column">
                {rightSectionIds
                  .map((id) => sectionById[id])
                  .filter(Boolean)
                  .map((section) => (
                    <SectionPanel key={section.id} title={section.title}>
                      {section.blocks.map((block) => (
                        <BlockRenderer key={block.id} block={block} />
                      ))}
                    </SectionPanel>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CsvAnalysisReportModal;
